import React, { useCallback } from 'react';

import { Typography } from '@rbilabs/universal-components';
import { useFocusEffect, useLocalSearchParams, usePathname } from 'expo-router';
import { StyleSheet } from 'react-native';

import { CenteredRowContainer } from 'components/layout/centered-row-container';
import { useAuthContext } from 'state/auth';
import { t } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';
import { primitive } from 'styles/constants/primitives';
import { routes } from 'utils/routing';
import { parseBool } from 'utils/string-To-Bool';

import {
  RewardsTabLink,
  RewardsTabLinkText,
  RewardsTabsBorderBottom,
} from './loyalty-rewards.styled';

// TODO switch to https://github.com/EvanBacon/expo-router-top-tabs
export const LoyaltyRewardsTabs: React.FC = () => {
  const enableLoyaltyProfile = useFlag(LaunchDarklyFlag.ENABLE_LOYALTY_PROFILE);

  const pathname = usePathname();
  const { showSignUp: _showSignUp = true } = useLocalSearchParams<{ showSignUp: string }>();
  const showSignUp = parseBool(_showSignUp);

  const { loyaltyUser } = useLoyaltyUser();

  const redeemTabKey = 'redeemTab';
  const historyTabKey = 'historyTab';
  const profileTabKey = 'profileTab';

  const isIncentiveSelected = pathname && !!pathname.split('/')[3];
  const showCtaBar = !isIncentiveSelected;
  const showTabs = !!showCtaBar;

  const redeemTab = {
    tabItemId: redeemTabKey,
    label: t(redeemTabKey),
    to: routes.rewardsList,
  };

  const historyTab = {
    tabItemId: historyTabKey,
    label: t(historyTabKey),
    to: routes.rewardsHistory,
  };

  const ProfileTab = {
    tabItemId: profileTabKey,
    label: t('profile'),
    to: routes.rewardsProfile,
  };

  const rewardsTabs = [redeemTab, ...(enableLoyaltyProfile ? [ProfileTab] : [historyTab])];

  const tabWidth = `${100 / rewardsTabs.length}%`;
  const { isAuthenticated, loading: isLoadingAuth, modalAuthIsOpen } = useAuthContext();
  const { openSignInModal } = useAuthContext();

  useFocusEffect(
    useCallback(() => {
      if (!isAuthenticated && !isLoadingAuth && showSignUp && !modalAuthIsOpen) {
        openSignInModal({
          ...(pathname.includes(routes.loyaltyActivateQuest)
            ? {
                navigateOnSuccessRoute: routes.loyaltyActivateQuest,
              }
            : {}),
        });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, isLoadingAuth, showSignUp, openSignInModal, pathname])
  );

  if (!showTabs) {
    return false;
  }

  return (
    <CenteredRowContainer testID="rewards-tabs" contentFlexDirection="row">
      {rewardsTabs.map(({ label, to, tabItemId }) => {
        const isActiveTab = pathname === to;
        return (
          <RewardsTabLink
            replace
            key={label}
            linkPath={!loyaltyUser ? `${to}?showSignUp=false` : to}
            testID={`tabbed-reward-${label}`}
            active={isActiveTab}
            minWidth={tabWidth}
          >
            <RewardsTabLinkText variant="headerThree" active={isActiveTab}>
              {label}
            </RewardsTabLinkText>
            {enableLoyaltyProfile && tabItemId === profileTabKey && (
              <Typography fontSize="2xs" color={primitive.bk.$white} style={styles.newLabel}>
                {t('new')}
              </Typography>
            )}
          </RewardsTabLink>
        );
      })}
      <RewardsTabsBorderBottom />
    </CenteredRowContainer>
  );
};

const styles = StyleSheet.create({
  newLabel: {
    backgroundColor: primitive.bk.$crunchyGreen,
    paddingHorizontal: primitive.spacing.$1,
    minHeight: 20,
    borderRadius: 4,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 2,
    marginLeft: 6,
  },
});
